import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SEO from './components/SEO';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import Header from './components/Header';
import HeroBanner from './components/HeroBanner';
import StatsSection from './components/StatsSection';
import FinancialSolutions from './components/FinancialSolutions';
import ABCCalculators from './components/ABCCalculators';
import CustomerStories from './components/CustomerStories';
import ABCDMoney from './components/ABCDMoney';
import Footer from './components/Footer';
import Loading from './components/ui/Loading';

// Lazy Load Pages
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const CompanyProfile = lazy(() => import('./components/CompanyProfile'));
const VisionValues = lazy(() => import('./components/VisionValues'));
const LocateUs = lazy(() => import('./components/LocateUs'));
const Careers = lazy(() => import('./components/Careers'));
const KhasraNumberBlog = lazy(() => import('./components/KhasraNumberBlog'));
const MortgageBlog = lazy(() => import('./components/MortgageBlog'));
const BuilderBuyerBlog = lazy(() => import('./components/BuilderBuyerBlog'));
const LoanWriteOffBlog = lazy(() => import('./components/LoanWriteOffBlog'));
const Sitemap = lazy(() => import('./components/Sitemap'));
const Login = lazy(() => import('./components/Login'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const EmployeeDashboard = lazy(() => import('./components/EmployeeDashboard'));
const PartnerDashboard = lazy(() => import('./components/PartnerDashboard'));
const ExampleFormWithValidation = lazy(() => import('./components/ExampleFormWithValidation'));
const EmployeeFormImproved = lazy(() => import('./components/EmployeeFormImproved'));
const ComponentShowcase = lazy(() => import('./components/ComponentShowcase'));
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));
const CustomerSignup = lazy(() => import('./components/CustomerSignup'));
const EmailConfirmation = lazy(() => import('./components/EmailConfirmation'));

function HomePage() {
  return (
    <>
      <SEO
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "We Care Finserv",
          "image": "https://wecarefinserv.com/logo192.png",
          "telephone": "+91 8855089863",
          "email": "admin@wecarefinserv.com",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "IN"
          },
          "url": "https://wecarefinserv.com"
        }}
      />
      <Header />
      <HeroBanner />
      <StatsSection />
      <FinancialSolutions />
      <ABCCalculators />
      <CustomerStories />
      <ABCDMoney />
      <Footer />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/employee-dashboard" element={<EmployeeDashboard />} />
              <Route path="/partner" element={<PartnerDashboard />} />
              <Route path="/customer-dashboard" element={<CustomerDashboard />} />
              <Route path="/customer-signup" element={<CustomerSignup />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/company-profile" element={<CompanyProfile />} />
              <Route path="/vision-values" element={<VisionValues />} />
              <Route path="/locate-us" element={<LocateUs />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/blog/what-is-khasra-number" element={<KhasraNumberBlog />} />
              <Route path="/blog/registered-mortgage-vs-equitable-mortgage" element={<MortgageBlog />} />
              <Route path="/blog/builder-buyer-agreement" element={<BuilderBuyerBlog />} />
              <Route path="/blog/loan-write-off-waiver" element={<LoanWriteOffBlog />} />

              {/* Auth Routes */}
              <Route path="/auth/confirm" element={<EmailConfirmation />} />

              {/* Demo & Admin Routes */}
              <Route path="/demo/form" element={<ExampleFormWithValidation />} />
              <Route path="/demo/components" element={<ComponentShowcase />} />
              <Route path="/employees/new" element={<EmployeeFormImproved />} />
              <Route path="/employees/:id/edit" element={<EmployeeFormImproved />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
